import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, TextField, Button, Box, Alert, CircularProgress,
  Tooltip, Typography, useMediaQuery, InputAdornment
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AccessTime,
  Info as InfoIcon,
  Person,
  Lock,
  Shield,
  ShieldOutlined,
  Public,
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { format } from 'date-fns';

function LoginForm({ onLogin, apiUrl }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Logon page settings (fetched publicly, no auth)
  const [logonSettings, setLogonSettings] = useState(null);
  const [clockNow, setClockNow] = useState(new Date());

  // 3D tilt state
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0 });
  const cardRef = useRef(null);

  // Welcome text reveal
  const [welcomeVisible, setWelcomeVisible] = useState(false);

  // IP check data
  const [ipStatus, setIpStatus] = useState(null);

  // Fetch logon page settings from public endpoint
  useEffect(() => {
    axios.get(`${apiUrl}/api/gui/settings/logon`, { timeout: 5000 })
      .then(res => setLogonSettings(res.data))
      .catch(err => console.warn('Could not fetch logon settings', err));
  }, [apiUrl]);

  // Fetch IP status (for banner)
  useEffect(() => {
    axios.get(`${apiUrl}/api/ip-check`, { timeout: 5000 })
      .then(res => setIpStatus(res.data))
      .catch(err => console.warn('Could not fetch IP status', err));
  }, [apiUrl]);

  // Live clock
  useEffect(() => {
    if (!logonSettings?.login_clock_enabled) return;
    const id = setInterval(() => setClockNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [logonSettings?.login_clock_enabled]);

  // Welcome text reveal delay
  useEffect(() => {
    if (logonSettings?.login_welcome_enabled) {
      const t = setTimeout(() => setWelcomeVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [logonSettings?.login_welcome_enabled]);

  // --- Helper to read a setting with default fallback ---
  const ls = (key, def = false) => (logonSettings ? (logonSettings[key] ?? def) : def);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (show2FA) {
        const response = await axios.post(`${apiUrl}/api/verify-2fa`, {
          username,
          code: twoFactorCode
        });

        if (response.data.access_token) {
          localStorage.setItem('token', response.data.access_token);
          onLogin(response.data.access_token);
        }
      } else {
        const response = await axios.post(`${apiUrl}/api/login`, {
          username,
          password
        });

        if (response.data.status === '2fa_required') {
          setShow2FA(true);
          setSuccessMessage('A verification code has been sent to your email.');
        } else if (response.data.access_token) {
          localStorage.setItem('token', response.data.access_token);
          onLogin(response.data.access_token);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- 3D Tilt handlers ---
  const handleMouseMove = useCallback((e) => {
    if (!ls('login_3d_tilt', true) || isMobile || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const tiltX = ((y - centerY) / centerY) * 8;
    const tiltY = ((centerX - x) / centerX) * 8;
    setTilt({ rotateX: tiltX, rotateY: tiltY });
  }, [ls, isMobile]);

  const handleMouseLeave = useCallback(() => {
    setTilt({ rotateX: 0, rotateY: 0 });
  }, []);

  // --- Computed styles & Animations ---
  const primaryColor = theme.palette.primary.main || '#00d4ff';
  const secondaryColor = theme.palette.secondary.main || '#ff4081';
  const successColor = theme.palette.success.main || '#00ff88';

  const logoPulse = keyframes`
    0%, 100% {
      filter: drop-shadow(0 0 8px rgba(0, 212, 255, 0.4));
      transform: scale(1);
    }
    50% {
      filter: drop-shadow(0 0 16px rgba(0, 212, 255, 0.6));
      transform: scale(1.03);
    }
  `;

  const glitchAnim = keyframes`
    0% { text-shadow: 0 0 5px rgba(0, 212, 255, 0.3); }
    20% { text-shadow: 3px 0 0 rgba(255, 64, 129, 0.5), -3px 0 0 rgba(0, 212, 255, 0.5); }
    40% { text-shadow: -3px 0 0 rgba(255, 64, 129, 0.5), 3px 0 0 rgba(0, 212, 255, 0.5); }
    60% { text-shadow: 3px 0 0 rgba(255, 64, 129, 0.5), -3px 0 0 rgba(0, 212, 255, 0.5); }
    80% { text-shadow: -3px 0 0 rgba(255, 64, 129, 0.5), 3px 0 0 rgba(0, 212, 255, 0.5); }
    100% { text-shadow: 0 0 5px rgba(0, 212, 255, 0.3); }
  `;

  const pulseText = keyframes`
    0%, 100% { opacity: 0.7; filter: brightness(1); }
    50% { opacity: 1; filter: brightness(1.1); }
  `;

  const typewriter = keyframes`
    0% { width: 0; opacity: 1; border-right: 3px solid transparent; }
    2% { border-right: 3px solid ${secondaryColor}; }
    50% { width: 100%; border-right: 3px solid ${secondaryColor}; }
    52% { border-right: 3px solid transparent; }
    100% { width: 100%; border-right: 3px solid transparent; }
  `;

  const bounceIn = keyframes`
    0% { transform: scale(0.7); opacity: 0; }
    60% { transform: scale(1.05); opacity: 1; }
    75% { transform: scale(0.95); }
    100% { transform: scale(1); opacity: 1; }
  `;

  const hologramBorder = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const binaryChar = keyframes`
    0%, 100% { opacity: 0; }
    50% { opacity: 0.7; }
  `;

  // —— Helper: build the CSS animation string for a named effect ——
  const getEffectAnimation = (effectName, infinite = false) => {
    const duration = infinite ? '4s' : '1.5s';
    const timing = infinite ? 'ease-in-out infinite' : 'ease-out';
    switch (effectName) {
      case 'glitch':
        return `${glitchAnim} ${duration} ${timing}`;
      case 'pulse':
        return `${pulseText} ${duration} ${timing}`;
      case 'typewriter':
        return `${typewriter} 3s ease-in-out`;
      case 'bounce':
        return `${bounceIn} ${duration} ${timing}`;
      case 'none':
      default:
        return 'none';
    }
  };

  // Resolve effect from settings
  const titleEffect = ls('login_title_effect', 'pulse');
  const welcomeEffect = ls('login_welcome_effect', 'pulse');

  const titleColor = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

  const baseFieldStyles = {
    position: 'relative',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
        boxShadow: `0 0 0 3px ${primaryColor}33`,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.65)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: primaryColor,
    },
    '& .MuiOutlinedInput-input': {
      color: 'white',
    },
  };

  const logoSrc = `${process.env.PUBLIC_URL}/watchtower.png`;

  // --- Card style with 3D tilt ---
  const cardStyle = {
    borderRadius: { xs: '24px', sm: '32px' },
    backgroundColor: 'rgba(7, 11, 22, 0.85)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(24px)',
    boxShadow: '0 40px 60px rgba(2, 5, 20, 0.8)',
    position: 'relative',
    overflow: 'hidden',
    py: 5,
    px: 4,
    transform: ls('login_3d_tilt', true) && !isMobile
      ? `perspective(1000px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg)`
      : 'none',
    transition: ls('login_3d_tilt', true) ? 'transform 0.1s ease-out' : 'none',
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      disableGutters
      sx={{ minHeight: '100vh', display: 'flex', position: 'relative', zIndex: 1 }}
    >
      <Box
        sx={{
          margin: 'auto',
          width: '100%',
          px: 3,
        }}
      >
        <Box
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          sx={cardStyle}
        >
          {/* Hologram rotating border (conic gradient ring) */}
          {ls('login_hologram_border', true) && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                padding: '4px',
                pointerEvents: 'none',
                zIndex: 0,
                background: `conic-gradient(from 0deg, ${primaryColor}, ${secondaryColor}, ${successColor}, ${primaryColor})`,
                animation: `${hologramBorder} 6s linear infinite`,
                filter: 'blur(1px)',
                opacity: 0.4,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '4px',
                  borderRadius: 'inherit',
                  backgroundColor: 'rgba(7, 11, 22, 0.85)',
                  zIndex: 1,
                },
              }}
            />
          )}

          {/* Inner gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.05) 30%, rgba(255, 255, 255, 0) 55%)',
              pointerEvents: 'none',
              opacity: 0.4,
            }}
          />

          {/* Floating binary chars for cyberpunk vibe */}
          {ls('login_hologram_border', true) && (
            <>
              {[...Array(12)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    color: `${primaryColor}44`,
                    left: `${15 + (i * 6) % 70}%`,
                    top: `${20 + (i * 7) % 60}%`,
                    animation: `${binaryChar} 3s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                    pointerEvents: 'none',
                  }}
                >
                  {Math.random() > 0.5 ? '1' : '0'}
                </Box>
              ))}
            </>
          )}

          {/* Logo + Title */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 4,
              position: 'relative',
            }}
          >
            <Box
              component="img"
              src={logoSrc}
              alt="logo"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                padding: 0.5,
                animation: `${logoPulse} 6s ease-in-out infinite`,
                filter: `drop-shadow(0 0 15px ${primaryColor}44)`,
                ...(ls('login_glitch_title', true) && titleEffect !== 'none' && {
                  animation: `${logoPulse} 6s ease-in-out infinite, ${getEffectAnimation(titleEffect, true)}`,
                  animationDelay: '0s, 0s',
                }),
              }}
            />
          </Box>

          {/* App Title text with configurable effect */}
          {ls('login_glitch_title', true) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
                  background: titleColor,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  letterSpacing: '4px',
                  textAlign: 'center',
                  animation: getEffectAnimation(titleEffect, true),
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  textTransform: 'uppercase',
                  textShadow: '0 0 8px rgba(0, 212, 255, 0.15)',
                }}
              >
                WatchTower
              </Typography>
            </Box>
          )}

          {/* Animated Welcome Text (appears below logo/title) */}
          {ls('login_welcome_enabled', true) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: welcomeVisible ? 4 : 0,
                opacity: welcomeVisible ? 1 : 0,
                transform: welcomeVisible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 0.5s ease-out',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 500,
                  letterSpacing: '2px',
                  textAlign: 'center',
                  animation: getEffectAnimation(welcomeEffect, true),
                  animationDelay: '0.5s',
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  opacity: welcomeEffect === 'pulse' ? 0.9 : 1,
                }}
              >
                {logonSettings?.login_welcome_text || 'Welcome to WatchTower'}
              </Typography>
            </Box>
          )}

          {!ls('login_welcome_enabled', true) && <Box mb={4} />}

          {/* Live Clock */}
          {ls('login_clock_enabled', true) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
                pointerEvents: 'none',
              }}
            >
              <AccessTime sx={{ fontSize: 16, color: primaryColor }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                {format(clockNow, 'HH:mm:ss')}
              </Typography>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                {format(clockNow, 'MMM d, yyyy')}
              </Typography>
            </Box>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  fontSize: '0.9rem',
                  backgroundColor: 'rgba(255, 92, 92, 0.08)',
                  border: '1px solid rgba(255, 92, 92, 0.3)',
                }}
              >
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  fontSize: '0.9rem',
                  backgroundColor: 'rgba(0, 212, 255, 0.08)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  color: primaryColor,
                  '& .MuiAlert-icon': {
                    color: primaryColor,
                  },
                }}
              >
                {successMessage}
              </Alert>
            )}

            {!show2FA ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  sx={baseFieldStyles}
                  InputProps={ls('login_icons_in_fields', true) ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                      </InputAdornment>
                    ),
                  } : undefined}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  sx={{
                    ...baseFieldStyles,
                    ...(ls('login_icons_in_fields', true) && {
                      '& .MuiOutlinedInput-root': {
                        paddingRight: '42px',
                      },
                    }),
                  }}
                  InputProps={ls('login_icons_in_fields', true) ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          tabIndex={-1}
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          size="small"
                          variant="text"
                          sx={{
                            color: showPassword ? primaryColor : 'rgba(255,255,255,0.4)',
                            padding: 0,
                            minWidth: 'auto',
                            width: 32,
                            height: 32,
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                          }}
                        >
                          {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                        </Button>
                      </InputAdornment>
                    ),
                  } : undefined}
                />
              </>
            ) : (
              <>
                <TextField
                  margin="normal"
                  fullWidth
                  id="username_display"
                  label="Username"
                  name="username_display"
                  value={username}
                  disabled
                  sx={{
                    ...baseFieldStyles,
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="twoFactorCode"
                  label="Verification Code"
                  type="text"
                  id="twoFactorCode"
                  autoFocus
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  disabled={loading}
                  placeholder="Enter verification code"
                  inputProps={{
                    maxLength: 9,
                    style: { textAlign: 'center', letterSpacing: '4px', fontSize: '1.2rem' }
                  }}
                  sx={baseFieldStyles}
                />

                <Button
                  onClick={() => {
                    setShow2FA(false);
                    setTwoFactorCode('');
                    setSuccessMessage('');
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    mt: 1,
                    '&:hover': {
                      color: primaryColor,
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  Back to Login
                </Button>
              </>
            )}

            {/* What's New Tooltip */}
            {ls('login_whats_new_enabled', true) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Tooltip
                  title={logonSettings?.login_whats_new_text || '🚀 New features: 2FA login, Glassmorphism UI, Live stats dashboard'}
                  placement="top"
                  arrow
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      backgroundColor: 'rgba(8, 12, 22, 0.95)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.8rem',
                      borderRadius: '12px',
                      backdropFilter: 'blur(10px)',
                    },
                    '& .MuiTooltip-arrow': {
                      color: 'rgba(8, 12, 22, 0.95)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      color: secondaryColor,
                      fontSize: '0.8rem',
                      '&:hover': { color: '#fff', opacity: 1 },
                      opacity: 0.8,
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 16 }} /> What's New
                  </Box>
                </Tooltip>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={18} sx={{ color: 'white' }} />
                ) : null
              }
              sx={{
                mt: show2FA ? 2 : 3,
                py: 1.5,
                background: `linear-gradient(160deg, ${primaryColor}33, ${secondaryColor}22)`,
                borderRadius: '999px',
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: '0.1em',
                boxShadow: '0 12px 26px rgba(9, 9, 27, 0.6)',
                overflow: 'hidden',
                position: 'relative',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(120deg, rgba(255,255,255,0.25), rgba(255,255,255,0))',
                  opacity: 0,
                  transition: 'opacity 0.4s ease',
                },
                '&:hover': {
                  background: `linear-gradient(160deg, ${primaryColor}44, ${secondaryColor}33)`,
                  '&:before': {
                    opacity: 1,
                  },
                },
                '&.Mui-disabled': {
                  background: 'rgba(255, 255, 255, 0.12)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  boxShadow: 'none',
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: '100%',
                  display: 'inline-flex',
                  justifyContent: 'center',
                }}
              >
                {!loading && (show2FA ? 'Verify Code' : 'Continue')}
              </Box>
            </Button>
          </Box>

          {/* IP Check Banner (below button, outside form) */}
          {ls('login_ip_check_banner', true) && ipStatus && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Alert
                severity={ipStatus.severity || 'info'}
                icon={
                  ipStatus.is_blocked ? <Shield />
                  : ipStatus.is_home ? <ShieldOutlined />
                  : ipStatus.is_trusted ? <ShieldOutlined />
                  : ipStatus.needs_2fa ? <Public />
                  : <InfoIcon />
                }
                sx={{
                  backgroundColor: {
                    error: 'rgba(255, 68, 68, 0.08)',
                    warning: 'rgba(255, 170, 0, 0.08)',
                    info: 'rgba(0, 212, 255, 0.08)',
                  }[ipStatus.severity || 'info'],
                  border: {
                    error: '1px solid rgba(255, 68, 68, 0.3)',
                    warning: '1px solid rgba(255, 170, 0, 0.3)',
                    info: '1px solid rgba(0, 212, 255, 0.3)',
                  }[ipStatus.severity || 'info'],
                  borderRadius: '12px',
                  '& .MuiAlert-icon': {
                    color: {
                      error: '#ff4444',
                      warning: '#ffaa00',
                      info: primaryColor,
                    }[ipStatus.severity || 'info'],
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {ipStatus.message}
                </Typography>
                {ipStatus.local_ip && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    Local IP: {ipStatus.local_ip}
                  </Typography>
                )}
                {ipStatus.isp && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    {[ipStatus.isp, ipStatus.city, ipStatus.country].filter(Boolean).join(', ')}
                  </Typography>
                )}
              </Alert>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default LoginForm;